// Doc section 3 "Traffic attribution": captures first-touch (persisted,
// never overwritten) and current-session attribution separately. Full
// referring URLs are never retained — only the referring *domain* — since
// a full URL can leak sensitive query strings from the referring page.

const FIRST_TOUCH_KEY = "hh_first_touch";

export interface Attribution {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  referrer_domain?: string;
  landing_page?: string;
}

function readUtm(params: URLSearchParams): Attribution {
  const attribution: Attribution = {};
  const source = params.get("utm_source") ?? params.get("source");
  const medium = params.get("utm_medium");
  const campaign = params.get("utm_campaign");
  const content = params.get("utm_content");
  const term = params.get("utm_term");
  if (source) attribution.source = source.slice(0, 128);
  if (medium) attribution.medium = medium.slice(0, 128);
  if (campaign) attribution.campaign = campaign.slice(0, 255);
  if (content) attribution.content = content.slice(0, 255);
  if (term) attribution.term = term.slice(0, 255);
  return attribution;
}

function referrerDomain(): string | undefined {
  if (!document.referrer) return undefined;
  try {
    const url = new URL(document.referrer);
    if (url.hostname === location.hostname) return undefined; // internal navigation, not a new attribution touch
    return url.hostname;
  } catch {
    return undefined;
  }
}

/** Only the path + safe query params (no PII/sensitive values) — see server-side scrubUrlPath for the second layer of defence. */
function safeLandingPage(): string {
  return location.pathname + (location.search ? location.search : "");
}

export function currentSessionAttribution(): Attribution {
  const utm = readUtm(new URLSearchParams(location.search));
  const domain = referrerDomain();
  const attribution: Attribution = { ...utm };
  if (domain) attribution.referrer_domain = domain;
  if (!attribution.source && domain) attribution.source = domain; // fall back to referrer as a coarse "source" when no UTM present
  attribution.landing_page = safeLandingPage();
  return attribution;
}

function readStorage(): Attribution | null {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
}
function writeStorage(value: Attribution): void {
  try {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable — first-touch just won't persist across visits, non-fatal */
  }
}

/** Returns first-touch attribution, capturing it now if this is the very first time we've seen this visitor. */
export function firstTouchAttribution(current: Attribution): Attribution {
  const existing = readStorage();
  if (existing) return existing;
  writeStorage(current);
  return current;
}
